import React, { useState, useEffect } from 'react';
import { Play, Mail, Phone, Instagram, Linkedin, Video, X, Settings } from 'lucide-react';
import AdminPanel from './AdminPanel';
import LoginModal from './LoginModal';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';
import { adminApi } from '../lib/adminApi';
import '../styles/portfolio.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Portfolio = () => {
  const [scrolled, setScrolled] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [sessionNotice, setSessionNotice] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Settings state
  const [heroSettings, setHeroSettings] = useState({
    background_type: 'image',
    background_url: '',
    title: 'Pedro Mota Visuals',
    subtitle: 'Video & Photography'
  });
  
  const [profileSettings, setProfileSettings] = useState({
    name: 'Pedro Mota',
    role: 'Audiovisual Director & Cinematographer',
    description: 'Com 15 anos de experiência na área audiovisual e licenciatura especializada, dedico-me a criar narrativas visuais que transcendem o comum.',
    profile_image_url: null,
    email: 'motapedro001@gmail.com',
    phone: '+351 966 656 362',
    instagram: '@pedromota.visuals',
    vimeo: 'pedromota',
    linkedin: 'pedro-mota-visuals'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchVideos();
    fetchSettings();
    checkAuthentication();
    const handleExpiredSession = () => {
      setShowAdmin(false);
      setSessionNotice('A sessão expirou. Entre novamente para continuar.');
      setShowLogin(true);
    };
    window.addEventListener('admin-session-expired', handleExpiredSession);
    return () => window.removeEventListener('admin-session-expired', handleExpiredSession);
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setHeroSettings(response.data.hero);
      setProfileSettings(response.data.profile);
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettingsLoaded(true);
    }
  };

  const checkAuthentication = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return false;
    setCheckingAuth(true);
    try {
      await adminApi.get('/api/auth/me', { silentAuthFailure: true });
      return true;
    } catch (error) {
      return false;
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleAdminClick = async () => {
    setSessionNotice('');
    if (await checkAuthentication()) {
      setShowAdmin(true);
    } else {
      setShowLogin(true);
    }
  };

  const handleLoginSuccess = () => {
    setSessionNotice('');
    setShowLogin(false);
    setShowAdmin(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setShowAdmin(false);
    setSessionNotice('');
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    fetchVideos();
    fetchSettings();
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedVideo]);

  return (
    <div className="portfolio-container">
      {/* Admin Access Button */}
      <button 
        data-testid="admin-access-button"
        disabled={checkingAuth}
        className="admin-access-btn"
        onClick={handleAdminClick}
        title="Painel Admin"
      >
        <Settings size={20} />
      </button>

      {/* Navigation */}
      <nav className={`nav-portfolio ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-content">
          <a data-testid="home-navigation-link" href="#home" className="nav-logo" onClick={() => scrollToSection('home')}>
            Pedro Mota Visuals
          </a>
          <ul className="nav-links">
            <li>
              <a data-testid="portfolio-navigation-link" href="#videos" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('videos'); }}>
                Portfólio
              </a>
            </li>
            <li>
              <a data-testid="about-navigation-link" href="#about" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>
                Sobre
              </a>
            </li>
            <li>
              <a data-testid="contact-navigation-link" href="#contact" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                Contacto
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        {settingsLoaded && heroSettings.background_url ? (
          <>
            {heroSettings.background_type === 'video' ? (
              <video 
                data-testid="hero-background-video"
                src={`${BACKEND_URL}${heroSettings.background_url}`}
                className="hero-background"
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img 
                src={heroSettings.background_url.startsWith('http') ? heroSettings.background_url : `${BACKEND_URL}${heroSettings.background_url}`}
                alt="Hero" 
                className="hero-background"
              />
            )}
          </>
        ) : (
          <div className="hero-background" style={{backgroundColor: '#000'}}></div>
        )}
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">{heroSettings.title}</h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">{heroSettings.subtitle}</p>
          <button data-testid="view-portfolio-button" className="hero-cta" onClick={() => scrollToSection('videos')}>
            Ver Portfólio
          </button>
        </div>
      </section>

      {/* Videos Section */}
      <section id="videos" className="section">
        <h2 className="section-title">Portfólio</h2>
        <p className="section-subtitle">
          Narrativas visuais que capturam a essência de cada momento
        </p>
        {loading ? (
          <div className="loading-state" data-testid="videos-loading-state">Carregando...</div>
        ) : videos.length === 0 ? (
          <div className="empty-state" data-testid="videos-empty-state">
            <p>Nenhum vídeo adicionado ainda.</p>
            <button data-testid="add-first-video-button" onClick={handleAdminClick} className="hero-cta">
              Adicionar Vídeos
            </button>
          </div>
        ) : (
          <div className="video-grid">
            {videos.map((video) => (
              <div 
                key={video.id} 
                data-testid={`portfolio-video-${video.id}`}
                className="video-card"
                onClick={() => setSelectedVideo(video)}
              >
                <img 
                  src={`${BACKEND_URL}${video.thumbnail_url}`}
                  alt={video.title} 
                  className="video-thumbnail"
                />
                <div className="play-button">
                  <Play />
                </div>
                <div className="video-overlay">
                  <h3 className="video-title" data-testid={`portfolio-video-title-${video.id}`}>{video.title}</h3>
                  <div className="video-meta">
                    <span className="video-category" data-testid={`portfolio-video-category-${video.id}`}>{video.category}</span>
                    <span data-testid={`portfolio-video-duration-${video.id}`}>{video.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <h2 className="section-title">Sobre</h2>
        <div className="about-content">
          {profileSettings.profile_image_url && (
            <img 
              src={`${BACKEND_URL}${profileSettings.profile_image_url}`}
              alt={profileSettings.name}
              className="about-image"
            />
          )}
          <h3 className="about-name" data-testid="profile-name">{profileSettings.name}</h3>
          <p className="about-role" data-testid="profile-role">{profileSettings.role}</p>
          <p className="about-description" data-testid="profile-description">{profileSettings.description}</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <h2 className="section-title">Contacto</h2>
        <div className="contact-content">
          <p className="section-subtitle">
            Vamos trabalhar juntos no seu próximo projeto
          </p>
          <div className="contact-methods">
            <div className="contact-method">
              <Mail className="contact-icon" />
              <p className="contact-label">Email</p>
              <a data-testid="contact-email-link" href={`mailto:${profileSettings.email}`} className="contact-link">
                <p className="contact-value" data-testid="contact-email-value">{profileSettings.email}</p>
              </a>
            </div>
            <div className="contact-method">
              <Phone className="contact-icon" />
              <p className="contact-label">Telemóvel</p>
              <a data-testid="contact-phone-link" href={`tel:${profileSettings.phone}`} className="contact-link">
                <p className="contact-value" data-testid="contact-phone-value">{profileSettings.phone}</p>
              </a>
            </div>
          </div>
          <div className="social-links">
            {profileSettings.instagram && (
              <a data-testid="instagram-link" aria-label="Instagram" href={`https://instagram.com/${profileSettings.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={24} />
              </a>
            )}
            {profileSettings.vimeo && (
              <a data-testid="vimeo-link" aria-label="Vimeo" href={`https://vimeo.com/${profileSettings.vimeo}`} target="_blank" rel="noopener noreferrer" className="social-link">
                <Video size={24} />
              </a>
            )}
            {profileSettings.linkedin && (
              <a data-testid="linkedin-link" aria-label="LinkedIn" href={`https://linkedin.com/in/${profileSettings.linkedin}`} target="_blank" rel="noopener noreferrer" className="social-link">
                <Linkedin size={24} />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p className="footer-text">
          © 2025 Pedro Mota Visuals. Todos os direitos reservados.
        </p>
      </footer>

      {/* Video Modal */}
      {selectedVideo && (
        <div data-testid="video-player-overlay" className="modal-overlay" onClick={() => setSelectedVideo(null)}>
          <div data-testid="video-player-modal" className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button data-testid="video-player-close-button" aria-label="Fechar vídeo" className="modal-close" onClick={() => setSelectedVideo(null)}>
              <X size={32} />
            </button>
            <video 
              data-testid="video-player"
              className="modal-video" 
              controls 
              autoPlay
              src={`${BACKEND_URL}${selectedVideo.video_url}`}
            >
              Your browser does not support the video tag.
            </video>
            <div className="modal-info">
              <h3 className="modal-title" data-testid="video-player-title">{selectedVideo.title}</h3>
              <div className="modal-meta">
                <span className="video-category" data-testid="video-player-category">{selectedVideo.category}</span>
                <span data-testid="video-player-duration">{selectedVideo.duration}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel */}
      {showAdmin && (
        <AdminPanel 
          onClose={() => setShowAdmin(false)}
          onUploadSuccess={handleUploadSuccess}
          onLogout={handleLogout}
        />
      )}

      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onLoginSuccess={handleLoginSuccess}
          notice={sessionNotice}
        />
      )}

      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
};

export default Portfolio;